import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { isStaffRole } from "@/types";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/auth/Login.vue"),
    meta: { public: true, title: "Login" },
  },
  {
    path: "/share/:token",
    name: "share",
    component: () => import("@/views/share/ShareView.vue"),
    meta: { public: true, title: "Credentials" },
  },
  {
    path: "/",
    component: () => import("@/layouts/AdminLayout.vue"),
    redirect: "/dashboard",
    children: [
      {
        path: "dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: { title: "Dashboard", icon: "Odometer" },
      },
      {
        path: "companies",
        name: "companies",
        component: () => import("@/views/companies/CompanyList.vue"),
        meta: { title: "Companies", icon: "OfficeBuilding" },
      },
      {
        path: "companies/:id",
        name: "company-detail",
        component: () => import("@/views/companies/CompanyDetail.vue"),
        meta: { title: "Company", hidden: true },
      },
      {
        path: "sessions",
        name: "sessions",
        component: () => import("@/views/sessions/SessionList.vue"),
        meta: { title: "Sessions", icon: "Files" },
      },
      {
        path: "sessions/:id",
        name: "session-detail",
        component: () => import("@/views/sessions/SessionDetail.vue"),
        meta: { title: "Session", hidden: true },
      },
      {
        path: "analytics/funnel",
        name: "analytics-funnel",
        component: () => import("@/views/analytics/Funnel.vue"),
        meta: { title: "Funnel", icon: "TrendCharts" },
      },
      {
        path: "analytics/verdicts",
        name: "analytics-verdicts",
        component: () => import("@/views/analytics/Verdicts.vue"),
        meta: { title: "Verdicts", icon: "DataAnalysis" },
      },
      {
        path: "analytics/performance",
        name: "analytics-performance",
        component: () => import("@/views/analytics/Performance.vue"),
        meta: { title: "Performance", icon: "Odometer" },
      },
      {
        path: "analytics/tenants",
        name: "analytics-tenants",
        component: () => import("@/views/analytics/Tenants.vue"),
        meta: { title: "Tenants", icon: "OfficeBuilding" },
      },
      // SDK telemetry — the client half of the funnel. Same audience as the
      // rest of the analytics surface; the API gates on an authenticated
      // backoffice user with no further role check.
      {
        path: "analytics/sdk",
        name: "analytics-sdk",
        component: () => import("@/views/analytics/SdkFunnel.vue"),
        meta: { title: "SDK Funnel", icon: "Iphone" },
      },
      {
        path: "analytics/sdk-devices",
        name: "analytics-sdk-devices",
        component: () => import("@/views/analytics/SdkDevices.vue"),
        meta: { title: "SDK Devices", icon: "Cellphone" },
      },
      // Rejected uploads — the 4xx/499 answers the SDK got while sending
      // frames. No role gate: a company viewer sees their own numbers, and
      // the scoping happens server side.
      {
        path: "analytics/upload-rejections",
        name: "analytics-upload-rejections",
        component: () => import("@/views/analytics/UploadRejections.vue"),
        meta: { title: "Upload Rejections", icon: "Upload" },
      },
      {
        path: "identities",
        name: "identities",
        component: () => import("@/views/identities/IdentityList.vue"),
        meta: { title: "Identities", icon: "Postcard", role: "super_admin" },
      },
      // Mobile app backoffice — every page is super-admin-only.
      {
        path: "mobile/users",
        name: "mobile-users",
        component: () => import("@/views/mobile/MobileUserList.vue"),
        meta: { title: "Mobile Users", icon: "User", role: "super_admin" },
      },
      {
        path: "mobile/devices",
        name: "mobile-devices",
        component: () => import("@/views/mobile/MobileDeviceList.vue"),
        meta: { title: "Mobile Devices", icon: "Cellphone", role: "super_admin" },
      },
      {
        path: "mobile/access-events",
        name: "mobile-access-events",
        component: () => import("@/views/mobile/AccessEventList.vue"),
        meta: { title: "Access Events", icon: "Clock", role: "super_admin" },
      },
      {
        path: "mobile/qr-requests",
        name: "mobile-qr-requests",
        component: () => import("@/views/mobile/QrRequestList.vue"),
        meta: { title: "QR Requests", icon: "Grid", role: "super_admin" },
      },
      // Service catalog — partner directory shown by the mobile app.
      {
        path: "catalog/services",
        name: "catalog-services",
        component: () => import("@/views/catalog/ServiceList.vue"),
        meta: { title: "Services", icon: "Shop", staff: true },
      },
      {
        path: "catalog/categories",
        name: "catalog-categories",
        component: () => import("@/views/catalog/ServiceCategoryList.vue"),
        meta: { title: "Service Categories", icon: "Collection", staff: true },
      },
      {
        path: "api-logs",
        name: "api-logs",
        component: () => import("@/views/api-logs/ApiLogList.vue"),
        meta: { title: "API Logs", icon: "Document", staff: true },
      },
      {
        path: "audit-logs",
        name: "audit-logs",
        component: () => import("@/views/audit/AuditLogList.vue"),
        meta: { title: "Audit Logs", icon: "Tickets", staff: true },
      },
      {
        path: "users",
        name: "users",
        component: () => import("@/views/users/UserList.vue"),
        meta: { title: "Users", icon: "User", role: "super_admin" },
      },
      {
        path: "settings",
        name: "settings",
        component: () => import("@/views/settings/Profile.vue"),
        meta: { title: "Settings", icon: "Setting" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: () => import("@/views/NotFound.vue"),
    meta: { public: true, title: "Not Found" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore();
  const isPublic = to.matched.some((r) => r.meta.public);

  if (isPublic) {
    // Already-authed user revisiting /login → send them home.
    if (to.name === "login" && auth.isAuthenticated) {
      return next({ name: "dashboard" });
    }
    return next();
  }

  if (!auth.isAuthenticated) {
    return next({ name: "login", query: { redirect: to.fullPath } });
  }

  // Role-gated routes (the API enforces this too). Bounce rather than render
  // an empty table, which reads as "no data" instead of "not allowed".
  const required = to.meta.role as string | undefined;
  if (required && auth.user?.role !== required) {
    return next({ name: "dashboard" });
  }

  // Staff-gated routes: surfaces that describe US rather than a tenant, so
  // there is no scoped version of them to show a customer. The API answers
  // STAFF_ONLY; bouncing here means a typed URL lands on the dashboard
  // instead of a toast the person cannot act on.
  if (to.meta.staff && !isStaffRole(auth.user?.role)) {
    return next({ name: "dashboard" });
  }

  next();
});

router.afterEach((to) => {
  const baseTitle = "FaceID Admin";
  const pageTitle = (to.meta?.title as string) || "";
  document.title = pageTitle ? `${pageTitle} · ${baseTitle}` : baseTitle;
});

export default router;
